import { useState, useEffect } from 'react';
import { X, Send, Phone, Mail, User, Building, AlertCircle, CheckCircle, ChevronLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { vendorInvitationService } from '../utils/vendors/invitationService';
import { VendorInviteRequest } from '../types/vendorInvitation';
import { db } from '../utils/firebase/client';
import { doc, getDoc } from 'firebase/firestore';

interface VendorInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: {
    id: string;
    name: string;
    email: string;
  };
  company: {
    id: string;
    name: string;
  };
}

export default function VendorInviteModal({
  isOpen,
  onClose,
  onSuccess,
  user,
  company,
}: VendorInviteModalProps) {
  const [formData, setFormData] = useState<VendorInviteRequest>({
    phoneNumber: '',
    email: '',
    contactName: '',
    companyName: '',
    services: '',
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // IMPORTANT: Fetch the actual company name from Firestore to ensure SMS uses correct name
  const [resolvedCompanyName, setResolvedCompanyName] = useState<string>(company.name);

  useEffect(() => {
    const fetchCompanyName = async () => {
      if (!company.id || company.id === 'default') return;

      try {
        // Try to get the company document directly
        const companyDoc = await getDoc(doc(db, 'companies', company.id));

        if (companyDoc.exists()) {
          const data = companyDoc.data();
          if (data?.companyName) {
            setResolvedCompanyName(data.companyName);
          }
        }
      } catch (error) {
        // Keep using the passed prop as fallback
      }
    };

    if (isOpen) {
      fetchCompanyName();
    }
  }, [isOpen, company.id]);

  if (!isOpen) return null;

  const handleChange = (field: keyof VendorInviteRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    handleChange('phoneNumber', formatted);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Phone number is required
    const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
    if (!phoneDigits) {
      errors.phoneNumber = 'Phone number is required';
    } else if (phoneDigits.length !== 10) {
      errors.phoneNumber = 'Phone number must be 10 digits';
    }

    // Email validation (if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {

      const result = await vendorInvitationService.createInvitation(
        formData,
        {
          id: user.id,
          name: user.name,
          email: user.email,
        },
        {
          id: company.id,
          name: resolvedCompanyName, // Use the resolved name from Firestore
        }
      );


      if (result.success) {
        toast.success(
          <div className="flex items-start space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-medium">Invitation Sent!</p>
              <p className="text-sm text-gray-600 mt-1">
                SMS sent to {formData.phoneNumber}. Vendor will receive a link after replying YES.
              </p>
            </div>
          </div>,
          { duration: 5000 }
        );

        // Reset form
        setFormData({
          phoneNumber: '',
          email: '',
          contactName: '',
          companyName: '',
          services: '',
          notes: '',
        });

        onSuccess();
        onClose();
      } else {
        console.error('Invitation failed:', result.message, result.error);
        toast.error(
          <div>
            <p className="font-medium">Failed to send invitation</p>
            <p className="text-sm mt-1">{result.message || 'Unknown error occurred'}</p>
            {result.error && <p className="text-xs mt-1 text-gray-600">Error: {result.error}</p>}
          </div>,
          { duration: 7000 }
        );
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(
        <div>
          <p className="font-medium">An unexpected error occurred</p>
          <p className="text-sm mt-1">{errorMessage}</p>
        </div>,
        { duration: 7000 }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Solid Background */}
      <div className="absolute inset-0 bg-slate-900" />

      <div className="relative min-h-full px-4 py-8 md:py-12">
        <div className="max-w-3xl mx-auto rounded-2xl border border-slate-700 bg-slate-900 shadow-xl overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-slate-800 border-b border-slate-700 px-6 py-5 flex items-center">
            <button
              onClick={onClose}
              className="flex items-center gap-1 mr-4 text-slate-400 hover:text-white transition-colors"
              disabled={isSubmitting}
            >
              <ChevronLeft className="w-6 h-6" />
              <span className="text-base font-medium">Back</span>
            </button>
            <div>
              <h2 className="text-xl font-bold text-white">Invite New Vendor</h2>
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            {/* Info Banner */}
            <div className="mx-6 mt-6 p-4 bg-blue-900/40 border border-blue-800 rounded-xl">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-100">
                  <p className="font-medium">How it works:</p>
                  <ol className="list-decimal list-inside mt-2 space-y-1 text-blue-200">
                    <li>Vendor receives SMS: "Reply YES to accept invitation"</li>
                    <li>After replying YES, they get a magic link to complete their profile</li>
                    <li>You'll be notified when their profile is ready for review</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 pb-32 space-y-6">
              {/* Phone Number - Required */}
              <div className="p-4 bg-slate-800 border border-slate-700 rounded-xl">
                <label className="block text-sm font-semibold text-white mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3 px-4 border border-slate-600 rounded-xl h-14 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-slate-900">
                  <Phone className="h-5 w-5 text-slate-500 flex-shrink-0" />
                  <input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phoneNumber}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    className={`flex-1 bg-transparent border-0 outline-none text-lg ${validationErrors.phoneNumber ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                      }`}
                    disabled={isSubmitting}
                  />
                </div>
                {validationErrors.phoneNumber && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.phoneNumber}
                  </p>
                )}
                <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                  SMS will be sent to this number
                </p>
              </div>

              {/* Contact Name - Optional */}
              <div className="p-4 bg-purple-50 dark:bg-slate-800 border border-purple-200 dark:border-slate-700 rounded-xl shadow-sm transition-all hover:shadow-md">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Contact Name <span className="text-gray-500 dark:text-slate-500">(Optional)</span>
                </label>
                <div className="flex items-center gap-3 px-4 border border-gray-300 dark:border-slate-700 rounded-xl h-14 focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent bg-white dark:bg-slate-800">
                  <User className="h-5 w-5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="John Smith"
                    value={formData.contactName}
                    onChange={(e) => handleChange('contactName', e.target.value)}
                    className="flex-1 bg-transparent border-0 outline-none text-lg text-gray-900 dark:text-white"
                    disabled={isSubmitting}
                  />
                </div>
                <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                  Used in SMS greeting: "Hi John!"
                </p>
              </div>

              {/* Company Name - Optional */}
              <div className="p-4 bg-green-50 dark:bg-slate-800 border border-green-200 dark:border-slate-700 rounded-xl shadow-sm transition-all hover:shadow-md">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Company Name <span className="text-gray-500 dark:text-slate-500">(Optional)</span>
                </label>
                <div className="flex items-center gap-3 px-4 border border-gray-300 dark:border-slate-700 rounded-xl h-14 focus-within:ring-2 focus-within:ring-green-500 focus-within:border-transparent bg-white dark:bg-slate-800">
                  <Building className="h-5 w-5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="ABC Plumbing Services"
                    value={formData.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    className="flex-1 bg-transparent border-0 outline-none text-lg text-gray-900 dark:text-white"
                    disabled={isSubmitting}
                  />
                </div>
                <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                  Pre-fills company name in their onboarding form
                </p>
              </div>

              {/* Services - Optional */}
              <div className="p-4 bg-amber-50 dark:bg-slate-800 border border-amber-200 dark:border-slate-700 rounded-xl shadow-sm transition-all hover:shadow-md">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Services Needed <span className="text-gray-500 dark:text-slate-500">(Optional)</span>
                </label>
                <Input
                  type="text"
                  placeholder="e.g., swabbing, water hauling, well servicing"
                  value={formData.services}
                  onChange={(e) => handleChange('services', e.target.value)}
                  className="h-14 text-lg bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus-visible:ring-blue-500"
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                  Included in SMS: "...wants you to become a vendor in OilWellX for [services]"
                </p>
              </div>

              {/* Email - Optional */}
              <div className="p-4 bg-sky-50 dark:bg-slate-800 border border-sky-200 dark:border-slate-700 rounded-xl shadow-sm transition-all hover:shadow-md">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Email Address <span className="text-gray-500 dark:text-slate-500">(Optional)</span>
                </label>
                <div className="flex items-center gap-3 px-4 border border-gray-300 dark:border-slate-700 rounded-xl h-14 focus-within:ring-2 focus-within:ring-sky-500 focus-within:border-transparent bg-white dark:bg-slate-800">
                  <Mail className="h-5 w-5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                  <input
                    type="email"
                    placeholder="john@abcplumbing.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`flex-1 bg-transparent border-0 outline-none text-lg ${validationErrors.email ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                      }`}
                    disabled={isSubmitting}
                  />
                </div>
                {validationErrors.email && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.email}
                  </p>
                )}
                <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                  For future email communications
                </p>
              </div>

              {/* Notes - Optional */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm transition-all hover:shadow-md">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Internal Notes <span className="text-gray-500 dark:text-slate-500">(Optional)</span>
                </label>
                <textarea
                  placeholder="e.g., 'Met at trade show', 'Recommended by pumper'"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  rows={3}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                  For your reference only - not shared with vendor
                </p>
              </div>
            </div>

            {/* Footer - Fixed at bottom with safe area for mobile nav */}
            <div className="sticky bottom-0 bg-gray-50 dark:bg-slate-900 px-6 py-4 pb-20 md:pb-4 border-t border-gray-200 dark:border-slate-800 flex items-center justify-end">
              <div className="flex space-x-3">
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="px-6 border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="px-8 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
